/** @file Vertex marquee gesture: a screen rectangle over the mesh; release selects the vertices inside. */
import { pointsInRect } from '@pose2d/editor-geometry/pointsInRect';
import type { ScreenRect } from '@pose2d/editor-geometry/types';
import type { Vec2 } from '@pose2d/math/types';
import { OVERLAY_STYLE } from '@pose2d/overlay/overlayStyle';
import type { ToolGesture, ViewportPointer } from '../../panels/viewport/viewportTypes';
import { selectModeOf } from '../select/selectModeOf';
import { meshGizmo } from './meshGizmo';
import type { MeshScene } from './meshScene';
import { selectVertices } from './selectVertices';

/** The rectangle's four sides as screen-pixel lines. */
function sides(r: ScreenRect): [Vec2, Vec2][] {
  const a: Vec2 = [r.x0, r.y0], b: Vec2 = [r.x1, r.y0], c: Vec2 = [r.x1, r.y1], d: Vec2 = [r.x0, r.y1];
  return [[a, b], [b, c], [c, d], [d, a]];
}

/** Drag from `press`: the wire plus the rectangle while moving; release replaces, adds (Shift) or toggles (Ctrl). */
export function vertexMarquee(s: MeshScene, press: ViewportPointer): ToolGesture {
  const { ctx } = s;
  let rect: ScreenRect = { x0: press.sx, y0: press.sy, x1: press.sx, y1: press.sy };
  return {
    move(p) {
      rect = { ...rect, x1: p.sx, y1: p.sy };
      ctx.setGizmo(meshGizmo(ctx.camera, s.points, s.target.mesh.triangles, () => OVERLAY_STYLE.vertex, sides(rect)));
    },
    release(p) {
      rect = { ...rect, x1: p.sx, y1: p.sy };
      const inside = pointsInRect({ coords: s.points, stride: 2, offset: 0, count: s.points.length / 2 }, ctx.camera, rect);
      selectVertices(s.target.attachment, inside, selectModeOf(press));
      return [];
    },
    cancel: () => undefined,
  };
}
